import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Plus,
  Loader2,
  Copy,
  CheckCircle2,
  KeyRound,
  ShieldAlert,
  Power,
  Trash2,
} from 'lucide-react';
import { apiFetch } from '../../lib/api';
import { notify } from '../../lib/notify';
import { Modal } from '../../components/Modal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import {
  createApiKeySchema,
  type ApiKeyScope,
  type CreateApiKeyInput,
} from '../../lib/schemas';

interface ApiKey {
  id: number;
  name: string;
  key_prefix: string;
  owner_id: number;
  scopes: ApiKeyScope[];
  platform: string;
  last_used_at: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

interface CreatedApiKey extends ApiKey {
  key: string; // plain-text — solo aparece al crear
}

const SCOPE_DESCRIPTIONS: Record<ApiKeyScope, string> = {
  'tickets:create': 'Crear tickets nuevos',
  'tickets:read': 'Leer tickets y mensajes públicos',
  'tickets:update': 'Modificar estado, prioridad y demás',
  'tickets:assign': 'Asignar tickets a agentes',
  'tickets:delete': 'Eliminar tickets (raramente recomendado)',
  'messages:create': 'Agregar mensajes a un ticket',
  'messages:read_internal': 'Leer notas internas del equipo',
  'users:manage': 'Gestionar usuarios del panel',
  'api_keys:manage': 'Crear / revocar otras API keys',
  'reports:read': 'Consultar reportes y métricas',
};

const SCOPES_BY_GROUP: Record<string, ApiKeyScope[]> = {
  'Para integraciones de cliente (widget / sitio externo)': ['tickets:create'],
  'Para integraciones de lectura (dashboards / reportes)': [
    'tickets:read',
    'reports:read',
  ],
  'Para integraciones de gestión (bots de agente / automatizaciones)': [
    'tickets:update',
    'tickets:assign',
    'messages:create',
    'messages:read_internal',
  ],
  'Avanzado (cuidado)': ['tickets:delete', 'users:manage', 'api_keys:manage'],
};

export default function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [reloadTick, setReloadTick] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState<CreatedApiKey | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKey | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ApiKey | null>(null);

  useEffect(() => {
    setLoading(true);
    apiFetch<ApiKey[]>('/api-keys')
      .then((data) => setKeys(data))
      .catch((err) => notify.apiError(err, 'No se pudieron cargar las API keys.'))
      .finally(() => setLoading(false));
  }, [reloadTick]);

  const refresh = () => setReloadTick((t) => t + 1);

  const doRevoke = async () => {
    if (!revokeTarget) return;
    try {
      await apiFetch(`/api-keys/${revokeTarget.id}/revoke`, { method: 'POST' });
      notify.success('API key revocada.', revokeTarget.name);
      refresh();
    } catch (err) {
      notify.apiError(err, 'No se pudo revocar.');
      throw err;
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiFetch(`/api-keys/${deleteTarget.id}`, { method: 'DELETE' });
      notify.success('API key eliminada.', deleteTarget.name);
      refresh();
    } catch (err) {
      notify.apiError(err, 'No se pudo eliminar.');
      throw err;
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-base font-semibold text-slate-900">API Keys</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            Credenciales para que sistemas externos consuman tu API. Cada key tiene una
            plataforma asociada y un conjunto de permisos.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-slate-900 hover:bg-slate-800 text-white"
        >
          <Plus size={14} />
          Generar nueva API key
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200">
        {loading && (
          <div className="px-5 py-12 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
            <Loader2 size={16} className="animate-spin" />
            Cargando…
          </div>
        )}
        {!loading && keys.length === 0 && (
          <div className="px-5 py-12 text-sm text-slate-500 text-center">
            Todavía no hay ninguna API key. Generá la primera para empezar a recibir
            tickets desde integraciones externas.
          </div>
        )}
        {!loading && keys.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50/70">
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3 font-medium">Nombre</th>
                  <th className="px-5 py-3 font-medium">Plataforma</th>
                  <th className="px-5 py-3 font-medium">Permisos</th>
                  <th className="px-5 py-3 font-medium">Último uso</th>
                  <th className="px-5 py-3 font-medium">Estado</th>
                  <th className="px-5 py-3 font-medium text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {keys.map((k) => (
                  <tr key={k.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <div className="font-medium text-slate-900">{k.name}</div>
                      <div className="text-[11px] text-slate-500 font-mono">
                        vipsoft_…{k.key_prefix}…
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-700">{k.platform}</td>
                    <td className="px-5 py-3">
                      <div className="flex flex-wrap gap-1 max-w-[280px]">
                        {k.scopes.slice(0, 3).map((s) => (
                          <span
                            key={s}
                            className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 font-mono"
                          >
                            {s}
                          </span>
                        ))}
                        {k.scopes.length > 3 && (
                          <span className="text-[10px] text-slate-500">
                            +{k.scopes.length - 3}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-3 text-xs text-slate-500">
                      {k.last_used_at ? formatDate(k.last_used_at) : 'Nunca'}
                    </td>
                    <td className="px-5 py-3">
                      {k.is_active ? (
                        <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                          Activa
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                          <span className="h-1.5 w-1.5 rounded-full bg-slate-400" />
                          Revocada
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {k.is_active && (
                          <button
                            onClick={() => setRevokeTarget(k)}
                            title="Revocar"
                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                          >
                            <Power size={14} />
                          </button>
                        )}
                        <button
                          onClick={() => setDeleteTarget(k)}
                          title="Eliminar permanentemente"
                          className="p-1.5 rounded-md text-slate-500 hover:bg-rose-50 hover:text-rose-700"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CreateApiKeyModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(created) => {
          setCreateOpen(false);
          setRevealedKey(created);
          refresh();
        }}
      />

      <RevealKeyModal data={revealedKey} onClose={() => setRevealedKey(null)} />

      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={(o) => !o && setRevokeTarget(null)}
        title="Revocar API key"
        description={
          revokeTarget && (
            <>
              La key dejará de funcionar inmediatamente. Las integraciones que la usen van a
              recibir error 401.
              <br />
              <strong className="text-slate-900">{revokeTarget.name}</strong>
              <span className="text-xs text-slate-500"> · {revokeTarget.platform}</span>
            </>
          )
        }
        confirmLabel="Revocar"
        tone="warning"
        onConfirm={doRevoke}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Eliminar API key permanentemente"
        description={
          deleteTarget && (
            <>
              Se borra completamente del sistema y no se puede recuperar. Si solo quieres
              cortar el acceso, usa &quot;Revocar&quot; en su lugar.
              <br />
              <strong className="text-slate-900">{deleteTarget.name}</strong>
            </>
          )
        }
        confirmLabel="Eliminar"
        tone="danger"
        onConfirm={doDelete}
      />
    </div>
  );
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleString('es-VE', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

// ---------- CreateApiKeyModal ----------

function CreateApiKeyModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (created: CreatedApiKey) => void;
}) {
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateApiKeyInput>({
    resolver: zodResolver(createApiKeySchema),
    defaultValues: { name: '', platform: '', scopes: [] },
  });

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  const onSubmit = async (values: CreateApiKeyInput) => {
    try {
      const created = await apiFetch<CreatedApiKey>('/api-keys', {
        method: 'POST',
        body: values,
      });
      reset();
      onCreated(created);
    } catch (err) {
      notify.apiError(err, 'No se pudo crear la API key.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Generar nueva API key"
      description="La key se mostrará una sola vez. Copiala y guardala en un lugar seguro."
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field
            label="Nombre interno"
            hint="Solo para reconocerla en este listado."
            error={errors.name?.message}
            input={
              <input
                type="text"
                placeholder="Ej: Sitio web ACME — producción"
                {...register('name')}
                className={inputClass(!!errors.name)}
              />
            }
          />
          <Field
            label="Plataforma"
            hint="Identificador del proyecto que la usa (ej: acme-web)."
            error={errors.platform?.message}
            input={
              <input
                type="text"
                placeholder="acme-web"
                {...register('platform')}
                className={inputClass(!!errors.platform)}
              />
            }
          />
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-600 mb-2">
            Permisos (scopes)
          </label>
          <Controller
            control={control}
            name="scopes"
            render={({ field }) => (
              <div className="space-y-3 max-h-72 overflow-y-auto border border-slate-200 rounded-lg p-3">
                {Object.entries(SCOPES_BY_GROUP).map(([group, scopes]) => (
                  <div key={group}>
                    <div className="text-[11px] font-medium text-slate-500 uppercase tracking-wide mb-1.5">
                      {group}
                    </div>
                    <div className="space-y-1.5">
                      {scopes.map((scope) => {
                        const checked = field.value.includes(scope);
                        return (
                          <label
                            key={scope}
                            className="flex items-start gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  field.onChange([...field.value, scope]);
                                } else {
                                  field.onChange(field.value.filter((s) => s !== scope));
                                }
                              }}
                              className="mt-0.5 h-3.5 w-3.5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div className="min-w-0">
                              <div className="text-xs font-mono text-slate-900">{scope}</div>
                              <div className="text-[11px] text-slate-500">
                                {SCOPE_DESCRIPTIONS[scope]}
                              </div>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          />
          {errors.scopes && (
            <p className="text-xs text-rose-600 mt-1">{errors.scopes.message}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-3 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40"
          >
            {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />}
            Generar key
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ---------- RevealKeyModal ----------

function RevealKeyModal({
  data,
  onClose,
}: {
  data: CreatedApiKey | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(data.key);
      setCopied(true);
      notify.success('Key copiada al portapapeles.');
      setTimeout(() => setCopied(false), 1500);
    } catch {
      notify.error('No se pudo copiar.', 'Seleccionala y copiala manualmente.');
    }
  };

  return (
    <Modal
      open={data !== null}
      onClose={onClose}
      title="API key generada"
      description="Guardala antes de cerrar — no la vas a poder recuperar después."
      size="lg"
    >
      {data && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-800 flex items-start gap-2">
            <ShieldAlert size={14} className="mt-0.5 shrink-0" />
            <span>
              Esta es la única vez que verás la key completa. Si la perdés, vas a tener que
              generar otra y actualizar las integraciones.
            </span>
          </div>

          <div className="grid grid-cols-2 gap-3 text-xs">
            <div>
              <div className="text-slate-500">Nombre</div>
              <div className="font-medium text-slate-900">{data.name}</div>
            </div>
            <div>
              <div className="text-slate-500">Plataforma</div>
              <div className="font-medium text-slate-900">{data.platform}</div>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              API Key (completa)
            </label>
            <div className="flex items-stretch gap-2">
              <input
                type="text"
                value={data.key}
                readOnly
                onFocus={(e) => e.currentTarget.select()}
                className="flex-1 px-3 py-2 text-sm font-mono rounded-lg border border-slate-200 bg-white"
              />
              <button
                onClick={handleCopy}
                className="px-3 py-2 text-sm rounded-lg bg-slate-900 hover:bg-slate-800 text-white inline-flex items-center gap-1.5"
              >
                {copied ? <CheckCircle2 size={14} /> : <Copy size={14} />}
                {copied ? 'Copiada' : 'Copiar'}
              </button>
            </div>
            <p className="text-[11px] text-slate-400 mt-1">
              Usala en el header HTTP <code className="font-mono">X-API-Key</code> o en{' '}
              <code className="font-mono">Authorization: Bearer …</code>.
            </p>
          </div>

          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
            <div className="text-xs font-medium text-slate-700 mb-2">
              Ejemplo: crear un ticket desde curl
            </div>
            <pre className="text-[11px] font-mono text-slate-700 overflow-x-auto whitespace-pre">{`curl -X POST https://soporte.vipsoft.cloud/api/v1/tickets \\
  -H "X-API-Key: ${data.key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "subject": "Problema con el login",
    "description": "No puedo ingresar desde móvil.",
    "customer": { "name": "Juan Pérez", "email": "juan@ejemplo.com" }
  }'`}</pre>
          </div>

          <div className="flex justify-end pt-2">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700"
            >
              Listo, ya la guardé
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ---------- helpers ----------

function inputClass(hasError: boolean) {
  return `w-full px-3 py-2 text-sm rounded-lg border bg-white focus:outline-none focus:ring-2 ${
    hasError ? 'border-rose-300 focus:ring-rose-500' : 'border-slate-200 focus:ring-blue-500'
  }`;
}

function Field({
  label,
  hint,
  error,
  input,
}: {
  label: string;
  hint?: string;
  error?: string;
  input: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {input}
      {error ? (
        <p className="text-xs text-rose-600 mt-1">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-slate-400 mt-1">{hint}</p>
      ) : null}
    </div>
  );
}
